import { Injectable } from '@angular/core';
import { ElementAttribute, ElementTagName } from '@models/editor';
import { CanvasInputMode } from './canvas-input-mode.service';
import { TriangleDrawHelperService } from './triangle-draw-helper.service';
import { CanvasService } from './canvas.service';
import { snapToGrid } from './connector/connector-utils';
import { SelectedElementsService } from './selected-elements.service';
import { UndoManagerService } from './undoMgr.service';

export enum PositionAxis {
    positionX = 'X',
    positionY = 'Y',
}

@Injectable({ providedIn: 'root' })
export class ElementPositionHandleHelperService {
    readonly POSITION_X = PositionAxis.positionX;
    readonly POSITION_Y = PositionAxis.positionY;

    constructor(
        private triangleService: TriangleDrawHelperService,
        private canvasService: CanvasService,
        private undoManagerService: UndoManagerService,
        private selectionService: SelectedElementsService
    ) {}

    handlePositionChange(name, value, selectedElements, noneSelect = false, saveHistory = true) {
        const axis = name === this.POSITION_X ? this.POSITION_X : name === this.POSITION_Y ? this.POSITION_Y : null;

        if (!axis) return;

        const { isX, coordAttr1, coordAttr2, ellipseAttr, radiusAttr, moveAttr } =
            this.getElementAttributesByAxis(axis);

        if (selectedElements.length > 0) {
            const first = selectedElements[0];
            let newValue;
            let currentValue;

            if (first.tagName === ElementTagName.path || first.tagName === ElementTagName.g) {
                const bbox = first.getBBox();
                currentValue = isX ? bbox.x : bbox.y;
                newValue = value;
            } else if (first.tagName === ElementTagName.ellipse) {
                const r = Number(first.getAttribute(radiusAttr));
                currentValue = Number(first.getAttribute(ellipseAttr));
                newValue = r + Number(value);
            } else if (first.tagName === ElementTagName.line) {
                currentValue = Number(first.getAttribute(coordAttr1));
                newValue = value;
            } else {
                currentValue = Number(first.getAttribute(moveAttr));
                newValue = value;
            }

            if (Number(currentValue) === Number(newValue)) {
                return;
            }
        }

        selectedElements.forEach(element => {
            if (element.tagName === ElementTagName.ellipse) {
                const previousValue = Number(element.getAttribute(ellipseAttr));
                const r = element.getAttribute(radiusAttr);
                element.setAttribute(ellipseAttr, Number(r) + Number(value));
                if (saveHistory)
                    this.undoManagerService.addCommandToHistory(
                        element,
                        {
                            [ellipseAttr]: previousValue,
                        },
                        'Position changed'
                    );
            } else if (element.tagName === ElementTagName.line) {
                const v1 = Number(element.getAttribute(coordAttr1));
                const v2 = Number(element.getAttribute(coordAttr2));
                const length = v2 - v1;
                element.setAttribute(coordAttr1, value);
                element.setAttribute(coordAttr2, Number(value) + length);
                if (saveHistory)
                    this.undoManagerService.addCommandToHistory(
                        element,
                        {
                            [coordAttr1]: v1,
                            [coordAttr2]: v2,
                        },
                        'Position changed'
                    );
            } else if (element.tagName === ElementTagName.path) {
                if (element.getAttribute(ElementAttribute.type) === CanvasInputMode.TRIANGLE) {
                    const D = element.getAttribute('d');
                    this.triangleService.setAbsolutePosition(element, value, axis);
                    if (saveHistory)
                        this.undoManagerService.addCommandToHistory(element, { d: D }, 'Path position changed');
                } else {
                    const canvas = this.canvasService.getCanvas();
                    const svgRoot = canvas.getSvgRoot();
                    const bbox = element.getBBox();
                    const current = this.getCurrentTranslate(element);
                    const initial = isX ? bbox.x : bbox.y;
                    let dx = isX ? value - initial : current.x;
                    let dy = !isX ? value - initial : current.y;

                    const { snapGridx, snapGridy } = this.setPathElementGridOption(canvas, isX, dx, dy);

                    canvas.updateTransformList(svgRoot, element, snapGridx, snapGridy);
                }
            } else if (element.tagName === ElementTagName.g) {
                const elementType = element.getAttribute(ElementAttribute.iCreateType);
                const connectorType = element.getAttribute(ElementAttribute.connectorType);
                const isPump = elementType === CanvasInputMode.DEFAULT_PUMP;
                const isValve = elementType === CanvasInputMode.DEFAULT_VALVE;
                const isFan = elementType === CanvasInputMode.DEFAULT_FAN;
                const isConnector = !!connectorType;

                if (isPump || isValve || isFan || isConnector) {
                    const canvas = this.canvasService.getCanvas();
                    const oldTransform = element.getAttribute(ElementAttribute.transform);
                    const bbox = element.getBBox();

                    if (isConnector) {
                        const tlist = element.transform.baseVal;
                        if (tlist.numberOfItems > 0) {
                            const m = tlist.getItem(0).matrix;
                            const newTx = isX ? Number(value) - m.a * bbox.x : m.e;
                            const newTy = !isX ? Number(value) - m.d * bbox.y : m.f;
                            element.setAttribute(
                                'transform',
                                `matrix(${m.a}, ${m.b}, ${m.c}, ${m.d}, ${newTx}, ${newTy})`
                            );
                        }
                    } else {
                        const current = this.getCurrentTranslate(element);
                        let dx = isX ? value - bbox.x : current.x;
                        let dy = !isX ? value - bbox.y : current.y;
                        const svgRoot = canvas.getSvgRoot();
                        canvas.updateTransformList(svgRoot, element, dx, dy);
                    }

                    if (saveHistory) {
                        this.undoManagerService.addCommandToHistory(
                            element,
                            { transform: oldTransform },
                            'Position changed'
                        );
                    }
                } else {
                    let canvas;
                    let batchCmd;
                    if (saveHistory) {
                        canvas = this.canvasService.getCanvas();
                        batchCmd = new canvas.history.BatchCommand('Change position group');
                    }

                    const parentElement = element.parentElement as unknown as SVGGraphicsElement;
                    const parentCTM = parentElement?.getScreenCTM?.();

                    let currentPos: number;

                    if (parentCTM) {
                        const rect = (element as unknown as Element).getBoundingClientRect();
                        const inv = parentCTM.inverse();
                        const tl = new DOMPoint(rect.left, rect.top).matrixTransform(inv);
                        currentPos = isX ? tl.x : tl.y;
                    } else {
                        const bbox = selectedElements[0].getBBox();
                        currentPos = isX ? bbox.x : bbox.y;
                    }

                    const groupMove = value - currentPos;
                    element.childNodes.forEach(element => {
                        this.moveToPositionGroup(
                            element,
                            groupMove,
                            isX ? this.POSITION_X : this.POSITION_Y,
                            saveHistory ? batchCmd : undefined,
                            saveHistory ? canvas : undefined
                        );
                    });
                    if (saveHistory) canvas.addCommandToHistory(batchCmd);
                }
            } else {
                const oldValue = Number(element.getAttribute(moveAttr));
                element.setAttribute(moveAttr, value);
                if (saveHistory)
                    this.undoManagerService.addCommandToHistory(
                        element,
                        {
                            [moveAttr]: oldValue,
                        },
                        'Position changed'
                    );
            }
        });

        if (!noneSelect) this.selectionService.setSelectedElements(selectedElements);
    }

    moveToPositionGroup(element: any, value: number, axis: PositionAxis, batchCmd: any, canvas?: any) {
        const { isX, moveAttr, coordAttr1, coordAttr2, ellipseAttr } = this.getElementAttributesByAxis(axis);

        if (element.tagName === ElementTagName.ellipse) {
            const coord = Number(element.getAttribute(ellipseAttr));
            element.setAttribute(ellipseAttr, coord + value);
            if (batchCmd)
                (batchCmd.addSubCommand(
                    new canvas.history.ChangeElementCommand(element, {
                        [ellipseAttr]: coord,
                    })
                ),
                    'Group position changed');
        } else if (element.tagName === ElementTagName.line) {
            const val1 = Number(element.getAttribute(coordAttr1));
            const val2 = Number(element.getAttribute(coordAttr2));
            element.setAttribute(coordAttr1, val1 + value);
            element.setAttribute(coordAttr2, val2 + value);
            if (batchCmd)
                (batchCmd.addSubCommand(
                    new canvas.history.ChangeElementCommand(element, {
                        [coordAttr1]: val1,
                        [coordAttr2]: val2,
                    })
                ),
                    'Line position changed');
        } else if (element.tagName === ElementTagName.path) {
            if (element.getAttribute(ElementAttribute.type) === CanvasInputMode.TRIANGLE) {
                const D = element.getAttribute('d');
                this.triangleService.setRelativePosition(element, value, axis);
                if (batchCmd)
                    batchCmd.addSubCommand(
                        new canvas.history.ChangeElementCommand(element, { d: D }, 'Path position changed')
                    );
            } else {
                const canvas = this.canvasService.getCanvas();
                const svgRoot = canvas.getSvgRoot();
                const current = this.getCurrentTranslate(element);
                const oldTransform = element.getAttribute('transform');
                let dx = isX ? current.x + value : current.x;
                let dy = !isX ? current.y + value : current.y;

                const { snapGridx, snapGridy } = this.setPathElementGridOption(canvas, isX, dx, dy);

                canvas.updateTransformList(svgRoot, element, snapGridx, snapGridy);

                if (batchCmd) {
                    batchCmd.addSubCommand(
                        new canvas.history.ChangeElementCommand(
                            element,
                            {
                                transform: oldTransform,
                            },
                            'Group position changed'
                        )
                    );
                }
            }
        } else if (element.tagName === ElementTagName.g) {
            element.childNodes.forEach(child => {
                this.moveToPositionGroup(child, value, axis, batchCmd, canvas);
            });
        } else {
            if (!(element instanceof SVGElement || element instanceof HTMLElement)) return;

            const hasTransform = element.getAttribute(ElementAttribute.transform);
            if (hasTransform) {
                const current = this.getCurrentTranslate(element as SVGGraphicsElement);
                const oldTransform = element.getAttribute(ElementAttribute.transform);
                const canvas = this.canvasService.getCanvas();
                const svgRoot = canvas.getSvgRoot();
                let dx = isX ? current.x + value : current.x;
                let dy = !isX ? current.y + value : current.y;
                canvas.updateTransformList(svgRoot, element as SVGGraphicsElement, dx, dy);
                if (batchCmd)
                    batchCmd.addSubCommand(
                        new canvas.history.ChangeElementCommand(
                            element,
                            {
                                transform: oldTransform,
                            },
                            'Group position changed'
                        )
                    );
            } else {
                const pos = Number(element.getAttribute(moveAttr));
                element.setAttribute(moveAttr, String(pos + value));
                if (batchCmd)
                    batchCmd.addSubCommand(
                        new canvas.history.ChangeElementCommand(
                            element,
                            {
                                [moveAttr]: pos,
                            },
                            'Position changed'
                        )
                    );
            }
        }
    }

    private getElementAttributesByAxis(axis: PositionAxis) {
        const isX = axis === this.POSITION_X;
        return {
            isX,
            moveAttr: isX ? ElementAttribute.x : ElementAttribute.y,
            coordAttr1: isX ? ElementAttribute.x1 : ElementAttribute.y1,
            coordAttr2: isX ? ElementAttribute.x2 : ElementAttribute.y2,
            ellipseAttr: isX ? ElementAttribute.cx : ElementAttribute.cy,
            radiusAttr: isX ? ElementAttribute.rx : ElementAttribute.ry,
        };
    }

    getCurrentTranslate(element: SVGGraphicsElement) {
        const tlist = element.transform.baseVal;

        for (let i = 0; i < tlist.numberOfItems; i++) {
            const item = tlist.getItem(i);
            if (item.type === SVGTransform.SVG_TRANSFORM_TRANSLATE) {
                const m = item.matrix;
                return { x: m.e, y: m.f };
            }
        }
        return { x: 0, y: 0 };
    }

    private setPathElementGridOption(canvas, isX: boolean, dx: number, dy: number) {
        let snapGridx = dx,
            snapGridy = dy;
        if (canvas.curShape.gridSnapping) {
            if (isX) snapGridx = snapToGrid(snapGridx, canvas);
            if (!isX) snapGridy = snapToGrid(snapGridy, canvas);
        }

        return { snapGridx, snapGridy };
    }
}
